<template>
  <FormItem
    :id="id || name"
    :label="inline ? '' : label"
    :label-width="labelWidth"
    :required="isRequired"
    :layout="layout"
    :error-message="errorMessage"
    :help-text="helpText"
    :no-margin="noMargin || isNested"
  >
    <template #before v-if="$slots.before">
      <slot name="before"></slot>
    </template>
    
    <div 
      class="form-switch-wrapper" 
      :class="{
        'form-switch-inline': inline,
        'is-invalid': errorMessage
      }"
    >
      <Switch
        :model-value="modelValue"
        :disabled="disabled"
        @update:model-value="handleChange"
      />
      
      <label v-if="inline" class="form-switch-label" :for="id || name">
        <span v-if="isRequired" class="text-danger me-1">*</span>
        {{ label }}
      </label>
      
      <slot></slot>
    </div>
    
    <template #after v-if="$slots.after">
      <slot name="after"></slot>
    </template>
  </FormItem>
</template>

<script setup>
import { computed, inject, onMounted, onBeforeUnmount, provide } from 'vue'
import FormItem from './FormItem.vue'
import Switch from './Switch.vue'

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  modelValue: {
    type: Boolean,
    default: false
  },
  name: {
    type: String,
    required: true
  },
  label: {
    type: String,
    default: ''
  },
  labelWidth: {
    type: String,
    default: undefined
  },
  disabled: {
    type: Boolean,
    default: false
  },
  helpText: {
    type: String,
    default: ''
  },
  inline: {
    type: Boolean,
    default: false
  },
  layout: {
    type: String,
    default: undefined
  },
  validateOnChange: {
    type: Boolean,
    default: true
  },
  noMargin: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'change'])

const formContext = inject('formContext', null)
const isNested = inject('isFormItemNested', false)
provide('isFormItemNested', true)

const layout = computed(() => props.layout || (formContext ? formContext.layout : 'vertical'))
const labelWidth = computed(() => props.labelWidth || (formContext ? formContext.labelWidth : undefined))

const isRequired = computed(() => {
  if (!formContext || !formContext.rules || !formContext.rules[props.name]) return false
  
  const rules = formContext.rules[props.name]
  return rules.some(rule => rule.required)
})

const errorMessage = computed(() => {
  if (!formContext || !formContext.errors) return ''
  return formContext.errors[props.name] || ''
})

const handleChange = (value) => {
  emit('update:modelValue', value)
  emit('change', value)
  
  if (props.validateOnChange && formContext && formContext.validateField) {
    formContext.validateField(props.name)
  }
}

onMounted(() => {
  if (formContext && formContext.registerFormItem) {
    formContext.registerFormItem(props.name, {
      validate: () => formContext.validateField(props.name)
    })
  }
})

onBeforeUnmount(() => {
  if (formContext && formContext.unregisterFormItem) {
    formContext.unregisterFormItem(props.name)
  }
})
</script>

<style scoped>
.form-switch-wrapper {
  display: flex;
  align-items: center;
}

.form-switch-inline {
  display: inline-flex;
  align-items: center;
  margin-right: 0.75rem;
}

.form-switch-label {
  margin-left: 0.5rem;
  margin-bottom: 0;
  cursor: pointer;
}
</style>



















